import { DependencyGraph } from '@koppla-tech/scheduling-engine';
import { min } from 'date-fns';

export type SortingElement = { id: string; startDate: Date; endDate: Date; sectionId: string };

export type SortingDependency = { id: string; from: { id: string }; to: { id: string } };

export type SortingProps = {
  elements: SortingElement[];
  dependencies: SortingDependency[];
  sections?: string[];
  /**
   * A map of special element groups, where the key is the group id and the value is an array of element ids.
   * This is used to identify special elements that should be sorted together. NOTE: Assumes that all groups are disjoint.
   */
  specialElementGroups?: Map<string, string[]>;
};

type Lane = {
  intervals: { startDate: Date; endDate: Date; elementId: string }[];
};

type Subgraph = { source: string; sink: string; graph: DependencyGraph };

/**
 * Composable to calculate the lanes for each element within a section that can be used to sort overlapping elements in a row.
 * The sorting is a graph-based priority sorting that considers dependencies between elements.
 * The general idea is:
 * 1. We identify all source nodes in the dependency graph of all elements and dependencies
 * 2. We then compute subgraphs using DFS starting from the sources (an element can then belong to multiple subgraphs)
 * 3. We sort the subgraphs based on their start and end dates and break ties using the default sorting function
 * 4. We then iterate each subgraph in that order and compute the overlap groups of the elements in the subgraph (an overlap group contains all transitive overlaps)
 * 5. We additionally sort the elements within the overlap group based on graph occurences + default sorting function (assumption: if an element is not only on the current graph, but on one that actually comes later, it is more likely to be a later event and should be sorted after the elements on the current graph)
 * 6. We iterate each element and add it to the first free lane based on the start and end dates
 * @param defaultSortFn - A default sorting function that is used for tie breaks and if no dependencies are found.
 * @returns A map of element ids to their lane indices.
 */
export const useChainBasedInRowSorting = (defaultSortFn: (aId: string, bId: string) => number) => {
  const elementLaneMap = ref(new Map() as Map<string, number>);
  const sectionMaxLanes = ref(new Map<string, number>());

  /**
   * Recalculates all lanes for the given sections. If no sections are provided, all lanes will be recalculated.
   * Special element groups are used to identify elements that should be sorted together. Those will be added to the same subgraph.
   * @param sections
   */
  const recalculate = ({
    elements,
    dependencies,
    sections,
    specialElementGroups,
  }: SortingProps) => {
    const elementsMap = new Map<string, SortingElement>(
      elements.map((element) => [element.id, element]),
    );
    const { elementsPerSections, dependenciesPerSections, specialElementGroupsPerSections } =
      groupBySections(elements, dependencies, elementsMap, specialElementGroups);

    const sectionsToRecalculate = sections ?? Array.from(elementsPerSections.keys());

    sectionsToRecalculate.forEach((sectionId) => {
      const sectionElements = elementsPerSections.get(sectionId) ?? [];
      const sectionDependencies = dependenciesPerSections.get(sectionId) ?? [];
      const sectionSpecialElementGroups =
        specialElementGroupsPerSections.get(sectionId) ?? new Map();
      const lanes: Lane[] = [];

      sectionMaxLanes.value.set(sectionId, 0);

      const subgraphs = identifySubgraphs(
        sectionElements,
        sectionDependencies,
        elementsMap,
        sectionSpecialElementGroups,
      );
      const sortedSubgraphs = sortSubgraphs(subgraphs, elementsMap, defaultSortFn);

      const visitedNodes = new Set<string>();
      const elementGraphOccurences = determineElementGraphOccurences(sortedSubgraphs);

      sortedSubgraphs.forEach(({ graph }) => {
        const overlapGroups = getOverlapGroups(
          graph
            .getNodes()
            .filter((n) => !visitedNodes.has(n.id))
            .map((n) => elementsMap.get(n.id)!),
        );
        overlapGroups.forEach((group) => {
          const sortedGroup = sortOverlapGroup(group, elementGraphOccurences, defaultSortFn);
          sortedGroup.forEach((element) => {
            if (visitedNodes.has(element.id)) {
              return;
            }

            const laneIdx = addToFreeLane(lanes, element);
            elementLaneMap.value.set(element.id, laneIdx);
            if ((sectionMaxLanes.value.get(sectionId) ?? 0) < laneIdx) {
              sectionMaxLanes.value.set(sectionId, laneIdx);
            }
            visitedNodes.add(element.id);
          });
        });
      });
    });
  };

  return { elementLaneMap, sectionMaxLanes, recalculate };
};

const groupBySections = (
  elements: SortingElement[],
  dependencies: SortingDependency[],
  elementsMap: Map<string, SortingElement>,
  specialElementGroups?: Map<string, string[]>,
) => {
  const elementsPerSections = new Map<string, SortingElement[]>();
  const dependenciesPerSections = new Map<string, SortingDependency[]>();
  const specialElementGroupsPerSections = new Map<string, Map<string, Set<string>>>();

  elements.forEach((element) => {
    if (!elementsPerSections.has(element.sectionId)) {
      elementsPerSections.set(element.sectionId, []);
    }
    elementsPerSections.get(element.sectionId)?.push(element);
  });
  dependencies.forEach((dependency) => {
    const fromSectionId = elementsMap.get(dependency.from.id)?.sectionId;
    const toSectionId = elementsMap.get(dependency.to.id)?.sectionId;
    if (!fromSectionId || !toSectionId || fromSectionId !== toSectionId) {
      return;
    }
    if (!dependenciesPerSections.has(fromSectionId)) {
      dependenciesPerSections.set(fromSectionId, []);
    }
    dependenciesPerSections.get(fromSectionId)?.push(dependency);
  });
  specialElementGroups?.forEach((specialElementIds, groupId) => {
    specialElementIds.forEach((elementId) => {
      const element = elementsMap.get(elementId);
      if (element) {
        if (!specialElementGroupsPerSections.has(element.sectionId)) {
          specialElementGroupsPerSections.set(element.sectionId, new Map());
        }
        const sectionSpecialElementGroups = specialElementGroupsPerSections.get(element.sectionId)!;
        if (!sectionSpecialElementGroups.has(groupId)) {
          sectionSpecialElementGroups.set(groupId, new Set());
        }
        sectionSpecialElementGroups.get(groupId)?.add(elementId);
      }
    });
  });
  return { elementsPerSections, dependenciesPerSections, specialElementGroupsPerSections };
};

const identifySubgraphs = (
  elements: SortingElement[],
  dependencies: SortingDependency[],
  elementsMap: Map<string, SortingElement>,
  specialElementGroups?: Map<string, Set<string>>,
): Subgraph[] => {
  const subgraphs: Subgraph[] = [];
  const elementSpecialGroupDict = new Map<string, string>();

  if (specialElementGroups) {
    const specialElementGroupsData = new Map<
      string,
      { elements: Map<string, SortingElement>; dependencies: Map<string, SortingDependency> }
    >();
    specialElementGroups.forEach((specialElementIds, groupId) => {
      const specialElements = new Map<string, SortingElement>();
      const specialDependencies = new Map<string, SortingDependency>();

      specialElementIds.forEach((id) => {
        const element = elementsMap.get(id);
        if (element) {
          specialElements.set(id, element);
          elementSpecialGroupDict.set(id, groupId);
        }
      });

      dependencies.forEach((dependency) => {
        if (specialElementIds.has(dependency.from.id) && specialElementIds.has(dependency.to.id)) {
          specialDependencies.set(dependency.id, dependency);
        }
      });

      specialElementGroupsData.set(groupId, {
        elements: specialElements,
        dependencies: specialDependencies,
      });
    });

    specialElementGroupsData.forEach((specialGroup) => {
      const specialElements = specialGroup.elements;
      const specialDependencies = specialGroup.dependencies;

      const subgraph = new DependencyGraph(specialElements, specialDependencies);
      const sources = subgraph.getSourceNodes();
      const source = sources.reduce((minElement, element) =>
        elementsMap.get(element)!.startDate.getTime() <
        elementsMap.get(minElement)!.startDate.getTime()
          ? element
          : minElement,
      );
      const sinks = subgraph.getSinkNodes();
      const sink = sinks.reduce((maxElement, element) =>
        elementsMap.get(element)!.endDate.getTime() > elementsMap.get(maxElement)!.endDate.getTime()
          ? element
          : maxElement,
      );
      subgraphs.push({ source, sink, graph: subgraph });
    });
  }

  // We remove cross-special graph dependencies, as elements can be idenfied based on the special group they belong to and shouldn't be influenced by other deps
  const nonCrossSpecialGroupDependencies = dependencies.filter((dependency) => {
    const fromGroup = elementSpecialGroupDict.get(dependency.from.id);
    const toGroup = elementSpecialGroupDict.get(dependency.to.id);
    return fromGroup === toGroup;
  });
  const completeGraph = new DependencyGraph(elements, nonCrossSpecialGroupDependencies);

  const sources = completeGraph.getSourceNodes();
  sources.forEach((source) => {
    const descendants = completeGraph.getDescendants([source], true);
    const subgraph = new DependencyGraph(
      Array.from(descendants).map((id) => ({ id })),
      dependencies.filter((d) => descendants.has(d.from.id) && descendants.has(d.to.id)),
    );
    const sinks = subgraph.getSinkNodes();
    const sink = sinks.reduce((lastSink, sink) =>
      elementsMap.get(sink)!.endDate.getTime() > elementsMap.get(lastSink)!.endDate.getTime()
        ? sink
        : lastSink,
    );
    subgraphs.push({ source, sink, graph: subgraph });
  });

  return subgraphs;
};

const sortSubgraphs = (
  subgraphs: Subgraph[],
  elementsMap: Map<string, SortingElement>,
  defaultSortFn: (aId: string, bId: string) => number,
) => {
  return subgraphs.sort((a, b) => {
    const aSourceElement = elementsMap.get(a.source);
    const bSourceElement = elementsMap.get(b.source);
    const aSinkElement = elementsMap.get(a.sink);
    const bSinkElement = elementsMap.get(b.sink);
    if (!aSourceElement || !bSourceElement || !aSinkElement || !bSinkElement) {
      return 0;
    }
    const aStart = aSourceElement.startDate;
    const bStart = bSourceElement.startDate;
    const sameStart = aStart.getTime() === bStart.getTime();
    if (!sameStart) {
      return aStart.getTime() - bStart.getTime();
    }
    const aEnd = aSinkElement.endDate;
    const bEnd = bSinkElement.endDate;
    const sameEnd = aEnd.getTime() === bEnd.getTime();
    if (!sameEnd) {
      return bEnd.getTime() - aEnd.getTime();
    }

    return defaultSortFn(a.source, b.source) || defaultSortFn(a.sink, b.sink);
  });
};

const determineElementGraphOccurences = (subgraphs: Subgraph[]) => {
  const elementGraphOccurences = new Map<string, Set<number>>();
  subgraphs.forEach(({ graph }, graphIdx) => {
    const nodes = graph.getNodes();
    nodes.forEach((node) => {
      if (!elementGraphOccurences.has(node.id)) {
        elementGraphOccurences.set(node.id, new Set());
      }
      elementGraphOccurences.get(node.id)!.add(graphIdx);
    });
  });
  return elementGraphOccurences;
};

const findCommonGraphs = (aOccurences: Set<number>, bOccurences: Set<number>) => {
  const commonGraphs = new Set<number>();
  aOccurences.forEach((graphIdx) => {
    if (bOccurences.has(graphIdx)) {
      commonGraphs.add(graphIdx);
    }
  });
  return commonGraphs;
};

const findLowestNonCommonGraph = (
  aOccurences: Set<number>,
  bOccurences: Set<number>,
  commonGraphs: Set<number>,
) => {
  const aGraphs = Array.from(aOccurences).filter((graphIdx) => !commonGraphs.has(graphIdx));
  const bGraphs = Array.from(bOccurences).filter((graphIdx) => !commonGraphs.has(graphIdx));
  return [
    aGraphs.length > 0 ? Math.min(...aGraphs) : -1,
    bGraphs.length > 0 ? Math.min(...bGraphs) : -1,
  ];
};

const sortOverlapGroup = (
  overlapGroup: SortingElement[],
  elementGraphOccurences: Map<string, Set<number>>,
  defaultSortFn: (aId: string, bId: string) => number,
) => {
  return overlapGroup.sort((a, b) => {
    const commonGraphs = findCommonGraphs(
      elementGraphOccurences.get(a.id)!,
      elementGraphOccurences.get(b.id)!,
    );
    const lowestCommonGraphIdx = commonGraphs.size === 0 ? -1 : Math.min(...commonGraphs);

    const [aLowestNonCommonGraphIdx, bLowestNonCommonGraphIdx] = findLowestNonCommonGraph(
      elementGraphOccurences.get(a.id)!,
      elementGraphOccurences.get(b.id)!,
      commonGraphs,
    );

    // If no common graphs, sort by lowest graph occurence
    if (lowestCommonGraphIdx === -1) {
      return aLowestNonCommonGraphIdx - bLowestNonCommonGraphIdx;
    }

    // If both elements don't have additional non-common graphs, use the default sort function
    if (aLowestNonCommonGraphIdx === -1 && bLowestNonCommonGraphIdx === -1) {
      return defaultSortFn(a.id, b.id);
    }
    // If only one of the elements has additional non-common graphs, sort by the lowest non-common graph
    return aLowestNonCommonGraphIdx - bLowestNonCommonGraphIdx;
  });
};

function findLaneInsertionIndex(lane: Lane, startDate: Date, endDate: Date): number {
  const intervals = lane.intervals;

  if (intervals.length === 0) {
    return -1;
  }
  if (endDate.getTime() <= intervals[0].startDate.getTime()) {
    return 0;
  }
  if (startDate.getTime() >= intervals[intervals.length - 1].endDate.getTime()) {
    return intervals.length;
  }

  for (let j = 1; j < intervals.length; j++) {
    const curr = intervals[j];
    const prev = intervals[j - 1];

    if (
      startDate.getTime() >= prev.endDate.getTime() &&
      endDate.getTime() <= curr.startDate.getTime()
    ) {
      return j;
    }
  }

  return -1;
}

function findFreeLane(
  lanes: Lane[],
  startDate: Date,
  endDate: Date,
): { laneIdx: number; intervalIdx: number } | undefined {
  if (!lanes.length) {
    return undefined;
  }

  for (let i = 0; i < lanes.length; i++) {
    const lane = lanes[i];

    const intervalIdx = findLaneInsertionIndex(lane, startDate, endDate);
    if (intervalIdx !== -1) {
      return { laneIdx: i, intervalIdx };
    }
  }

  return undefined;
}

function addToFreeLane(lanes: Lane[], element: SortingElement): number {
  const freeLane = findFreeLane(lanes, element.startDate, element.endDate);
  if (freeLane) {
    const { laneIdx, intervalIdx } = freeLane;
    lanes[laneIdx].intervals.splice(intervalIdx, 0, {
      startDate: element.startDate,
      endDate: element.endDate,
      elementId: element.id,
    });
    return laneIdx;
  } else {
    lanes.push({
      intervals: [
        { startDate: element.startDate, endDate: element.endDate, elementId: element.id },
      ],
    });
    return lanes.length - 1;
  }
}

/**
 * Returns an array of "overlap groups". Each group is an array of SortingElement objects
 * where any element in the group can be reached by traversing overlaps (direct or indirect).
 *
 * Two elements overlap if:
 *    elementA.startDate < elementB.endDate && elementB.startDate < elementA.endDate
 * (noting that the end date is exclusive).
 */
export function getOverlapGroups(events: SortingElement[]): SortingElement[][] {
  // Adjacency list: which events (by index) each event overlaps
  const adjacencyList: number[][] = new Array(events.length).fill(null).map(() => []);

  function overlaps(e1: SortingElement, e2: SortingElement): boolean {
    return e1.startDate < e2.endDate && e2.startDate < e1.endDate;
  }

  // 1. Build graph edges
  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      if (overlaps(events[i], events[j])) {
        adjacencyList[i].push(j);
        adjacencyList[j].push(i);
      }
    }
  }

  // 2. Find connected components via DFS
  const visited = new Array(events.length).fill(false);
  const result: SortingElement[][] = [];

  function dfs(startIndex: number, group: number[]) {
    const stack = [startIndex];
    visited[startIndex] = true;
    while (stack.length > 0) {
      const node = stack.pop()!;
      group.push(node);
      for (const neighbor of adjacencyList[node]) {
        if (!visited[neighbor]) {
          visited[neighbor] = true;
          stack.push(neighbor);
        }
      }
    }
  }

  // 3. For each event, if not visited, start a DFS to get its entire overlap group
  for (let i = 0; i < events.length; i++) {
    if (!visited[i]) {
      const groupIndices: number[] = [];
      dfs(i, groupIndices);
      const groupEvents = groupIndices.map((idx) => events[idx]);
      result.push(groupEvents);
    }
  }

  return result.sort((a, b) => {
    const aMinStart = min(a.map((e) => e.startDate));
    const bMinStart = min(b.map((e) => e.startDate));
    return aMinStart.getTime() < bMinStart.getTime() ? -1 : 1;
  });
}
